import { LOCATION_CHANGE } from 'connected-react-router';
import { showWarning } from '@domain/auth/auth.utils';
import { fetchLoggedUser } from '@domain/logged-user/logged-user.state';

import { getCompaniesAndCompaniesAccounts } from '@domain/company/company-account/company-account.state';
import { promiseTimeout } from '@domain/auth/create-profile/create-profile.utils';
import { showErrorToast } from '@shared/toasts';
import { setSentryUser } from '@domain/auth/sign-in/sign-in.utils';
import { createMiddleware } from '../../../shared/redux';
import { match } from '../../../shared/utils/match';
import * as AuthService from '../auth-service';
import {
    getSignInData,
    isSignInFromSignUp,
    issueChallengeSignIn,
    raiseSignInInvalidPasswordException,
    raiseSignInNotAuthorized,
    raiseSignInPasswordResetRequired,
    raiseSignInYupException,
    raiseSignInUnknownException,
    raiseSignInUserNotFound,
    raiseValidationException,
    rejectSignIn,
    resetSignInFlags,
    setSignInFlags,
    setUser,
    raiseNetworkException,
    SIGN_IN,
    SIGN_IN_ACCEPTED,
    SIGN_IN_CHALLENGE_ISSUED,
    SIGN_IN_REJECTED,
    SIGN_IN_NETWORK_EXCEPTION_RAISED,
    formatExceptionMessage,
    raiseInvalidParameterException,
    getSignInFlags,
    resetSignInLoading, SIGN_IN_FEDERATED, signInFederated, SIGN_IN_USER_SET,
} from './sign-in.state';
import { issueChallengeSmsMfa } from '../challenge-sms-mfa/challenge-sms-mfa-state';
import { issueChallengeSoftwareTokenMfa } from '../challenge-software-token-mfa/challenge-software-token-mfa-state';
import { issueChallengeMfaSetup } from '../challenge-mfa-setup/challenge-mfa-setup-state';
import { issueChallengeNewPasswordRequired } from '../challenge-new-password-required/challenge-new-password-required-state';
import { ROUTES } from '../auth.constants';
import { redirectToHome, redirectToRoute } from '../auth-base-state';
import {
    fetchUserFeatures,
} from '../../_redux/pareto-ads-app-redux/action/action';
import { fetchTeamMembers } from '../../team/team.state';
import { issueSignUpConfirmation, setSignUpData } from '../sign-up/sign-up-state';
import {
    integratePlatformIfNeeded,
    retrieveIntegrationPlatformInfoIfNeeded,
    setIntegrationProperties,
} from '../../integrations/integrations-properties.state';
import { redirectToSplashScreen } from '../splash-screen/splash-screen-state';
import { setIsSignUpDataComplete } from '../check-sign-up-data/check-sign-up-data.state';
import { getUserConfig } from '../../user-config/user-config.state';
import { INVALID_EMAIL_OR_NOT_FOUND, SIGN_IN_TEXT } from './sign-in.constants';
import { getTessPageCaller, isTessPagesCalling } from '../check-sign-up-data/check-sign-up-data.middlewares';

export const signInMiddleware = createMiddleware({
    [LOCATION_CHANGE]: (_, action) => {
        const locationSearch = action.payload.location?.search.toString();

        // This may change in the future depending on newer Federated Settings.
        const isFederatedLogin = locationSearch.includes('code=')
            && action.payload.location?.pathname?.includes(ROUTES.SIGN_IN);

        const isAzureError = locationSearch.includes('AZURE_IDP_ERROR');
        const isEmailMissing = locationSearch.includes('EMAIL_MISSING');

        if (isFederatedLogin) {
            return [
                setSignInFlags({
                    loadingFederatedLogin: true,
                }),
                redirectToSplashScreen(),
            ];
        }

        if (isAzureError && isEmailMissing) {
            return [showErrorToast({
                message: SIGN_IN_TEXT.AZURE_ERROR_EMAIL_MISSING,
                options: {
                    autoClose: false,
                },
            })];
        }

        const pathname = action.payload.location ? action.payload.location.pathname : '';
        return match(() => pathname, {
            [ROUTES.SIGN_IN]: () => {
                if (!action || !action.payload) return [];

                const { search } = action.payload.location;
                const params = new URLSearchParams(search);
                const platform = params.get('platform');
                const platformId = params.get('platformId');
                const hasPlatformIntegration = !!platform;

                return AuthService.currentAuthenticatedUser()
                    .then(() => [
                        setSignInFlags({
                            platformIntegration: hasPlatformIntegration,
                            passwordStep: false,
                        }),
                        setIntegrationProperties({ name: platform, id: platformId }),
                        integratePlatformIfNeeded(),
                        redirectToHome(),
                    ])
                    .catch(() => {
                        if (isTessPagesCalling()) {
                            getTessPageCaller()?.postMessage(JSON.stringify({
                                type: '@tess-pages/NOT_AUTHENTICATED',
                            }), '*');
                        }

                        return [
                            setSignInFlags({
                                platformIntegration: hasPlatformIntegration,
                                passwordStep: false,
                            }),
                            setIntegrationProperties({ name: platform, id: platformId }),
                            retrieveIntegrationPlatformInfoIfNeeded(),
                        ];
                    });
            },
        })();
    },
    [SIGN_IN_FEDERATED]: () => (
        promiseTimeout(AuthService.currentAuthenticatedUser().then(user => [
            setUser({ user }),
            setSignInFlags({
                loadingFederatedLogin: false,
            }),
            redirectToSplashScreen(),
        ]).catch(() => signInFederated()), 1000)
    ),
    [SIGN_IN]: async (state, action) => {
        const { passwordStep, fromSignUp, fromInvite } = getSignInFlags(state);
        const { username, password } = action.payload;

        const federatedLoginConfig = await AuthService.getFederatedLoginConfigByEmail(username);
        if (!federatedLoginConfig && !passwordStep && username && !fromSignUp && !fromInvite) {
            return [
                resetSignInLoading(),
                setSignInFlags({ passwordStep: true })];
        }

        return (
            AuthService.signIn({
                username,
                password,
                federatedLoginConfig,
            })
                .then((user) => {
                    // When is a federated login the user is not loaded yet.
                    if (federatedLoginConfig) return [];

                    return user.challengeName
                        ? issueChallengeSignIn({ user })
                        : [
                            setUser({ user }),
                            redirectToSplashScreen(),
                        ];
                })
                .catch(error => rejectSignIn({
                    code: error.code || 'UnknownException',
                    error,
                    fromSignUp: isSignInFromSignUp(state),
                }))
        );
    },
    [SIGN_IN_USER_SET]: (_, action) => {
        setSentryUser(action);
    },
    [SIGN_IN_ACCEPTED]: (_, action) => [
        // @TODO(#497) : refactor all fetch actions into single action flow and use axios-middleware
        setIsSignUpDataComplete({ isComplete: true }),
        integratePlatformIfNeeded(),
        fetchLoggedUser(),
        fetchTeamMembers(),
        fetchUserFeatures(),
        getUserConfig(),
        getCompaniesAndCompaniesAccounts(),
        resetSignInFlags(),
        redirectToRoute({ route: action.payload.landingRoute }),
    ],
    [SIGN_IN_REJECTED]: match((_, action) => action.payload.code, {
        UserNotConfirmedException: state => [
            setSignUpData({ data: getSignInData(state) }),
            issueSignUpConfirmation({ username: getSignInData(state).username }),
        ],
        PasswordResetRequiredException: (_, action) => (
            raiseSignInPasswordResetRequired({ error: action.payload.error })
        ),
        NotAuthorizedException: (_, action) => (
            raiseSignInNotAuthorized({ error: formatExceptionMessage(action.payload.error) })
        ),
        UserNotFoundException: () => (
            raiseSignInUserNotFound({ error: { message: INVALID_EMAIL_OR_NOT_FOUND } })
        ),
        InvalidPasswordException: (_, action) => (
            raiseSignInInvalidPasswordException({ error: action.payload.error })
        ),
        YupException: (_, action) => (
            raiseSignInYupException({ error: action.payload.error })
        ),
        UnknownException: (_, action) => (
            raiseSignInUnknownException({ error: action.payload.error })
        ),
        UsernameEmptyException: (_, action) => (
            raiseValidationException({ error: action.payload.error })
        ),
        PasswordEmptyException: (_, action) => (
            raiseValidationException({ error: action.payload.error })
        ),
        NetworkError: (_, action) => (
            raiseNetworkException({ error: action.payload.error })
        ),
        InvalidParameterException: () => (
            raiseInvalidParameterException({ error: { message: INVALID_EMAIL_OR_NOT_FOUND } })
        ),
    }),
    [SIGN_IN_CHALLENGE_ISSUED]: match((_, action) => action.payload.user.challengeName, {
        SMS_MFA: () => issueChallengeSmsMfa(),
        SOFTWARE_TOKEN_MFA: () => issueChallengeSoftwareTokenMfa(),
        NEW_PASSWORD_REQUIRED: () => issueChallengeNewPasswordRequired(),
        MFA_SETUP: () => issueChallengeMfaSetup(),
    }),
    [SIGN_IN_NETWORK_EXCEPTION_RAISED]: (_, action) => showWarning(action),
});
